import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, X } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../components/Button.js';
import { ApiError } from '../../lib/api.js';

const inputClass =
  'border-hairline bg-base/60 text-ink focus:border-accent/50 h-11 w-full rounded-lg border px-3 text-sm transition';

/**
 * Self-service password change, opened from the user menu. A compact centred
 * modal portalled to the document body — the top bar's backdrop-blur would
 * otherwise trap a `fixed` element inside the header. The new password must be
 * at least 10 characters (matched on the server) and confirmed, so a typo does
 * not lock anyone out. Changing it signs out every other device.
 */
export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  function close() {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    setBusy(false);
    setDone(false);
    onClose();
  }

  const mismatch = confirm.length > 0 && next !== confirm;
  const valid = current.length > 0 && next.length >= 10 && next === confirm;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    setError(null);
    setBusy(true);
    try {
      await changePassword(current, next);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Klarte ikke å bytte passord. Prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={close}
            aria-hidden
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Bytt passord"
            className="menu-surface relative w-full max-w-md p-5"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mb-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-ink text-base">Bytt passord</h2>
                <p className="text-ink-soft mt-1 text-sm">
                  {done
                    ? 'Passordet er byttet.'
                    : 'Nytt passord må være minst 10 tegn. Andre enheter blir logget ut.'}
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Lukk"
                className="text-ink-soft hover:bg-raised hover:text-ink -mr-1 -mt-1 rounded-lg p-2 transition"
              >
                <X size={18} aria-hidden />
              </button>
            </div>

            {done ? (
              <>
                <div className="text-ink-soft flex items-start gap-3 text-sm">
                  <CheckCircle2 size={18} className="text-success mt-0.5 shrink-0" aria-hidden />
                  <p>
                    Passordet ditt er oppdatert. Du er fortsatt logget inn her, men eventuelle
                    andre enheter må logge inn på nytt med det nye passordet.
                  </p>
                </div>
                <div className="mt-5 flex justify-end">
                  <Button variant="primary" size="sm" onClick={close}>
                    Lukk
                  </Button>
                </div>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="current-password" className="label-micro mb-1.5 block">
                    Nåværende passord
                  </label>
                  <input
                    id="current-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    autoFocus
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="new-password" className="label-micro mb-1.5 block">
                    Nytt passord
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    className={inputClass}
                  />
                  <p className="text-ink-soft/70 mt-1 text-xs">Minst 10 tegn.</p>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="label-micro mb-1.5 block">
                    Bekreft nytt passord
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className={inputClass}
                  />
                  {mismatch && <p className="text-danger mt-1 text-xs">Passordene er ikke like.</p>}
                </div>

                {error && (
                  <p
                    role="alert"
                    className="border-danger/30 bg-danger/10 text-danger rounded-lg border px-3 py-2 text-sm"
                  >
                    {error}
                  </p>
                )}

                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button type="button" variant="ghost" size="sm" onClick={close}>
                    Avbryt
                  </Button>
                  <Button type="submit" variant="primary" size="sm" disabled={!valid} loading={busy}>
                    Lagre nytt passord
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
